<script>
  import Layout from '../components/Layout.svelte';
  import { onMount } from 'svelte';

  // ---- category presentation -------------------------------------------------
  const CATEGORIES = [
    { key: 'active_user',    label: 'Active',        color: 'emerald' },
    { key: 'trial',          label: 'Trial',         color: 'blue' },
    { key: 'trial_expired',  label: 'Trial expired', color: 'red' },
    { key: 'prospect_warm',  label: 'Warm',          color: 'orange' },
    { key: 'prospect_cold',  label: 'Cold',          color: 'slate' }
  ];
  const badgeClass = {
    emerald: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    blue:    'bg-blue-100 text-blue-800 border-blue-200',
    red:     'bg-red-100 text-red-800 border-red-200',
    orange:  'bg-orange-100 text-orange-800 border-orange-200',
    slate:   'bg-slate-100 text-slate-700 border-slate-200'
  };
  const catColor = (key) => (CATEGORIES.find((c) => c.key === key) || { color: 'slate' }).color;
  const catLabel = (key) => (CATEGORIES.find((c) => c.key === key) || { label: key }).label;

  let user = null;
  let isAdmin = false;
  let loading = true;
  let error = null;
  let companies = [];
  let view = 'board'; // 'board' | 'actions'
  let actionTasks = [];
  let busy = null; // label of the running bulk action
  let toast = null;

  // detail drawer
  let selected = null;          // company summary object
  let detail = null;            // { company, contacts, signals, tasks }
  let detailLoading = false;
  let overrideCat = '';
  let overrideReason = '';

  $: byCategory = CATEGORIES.map((c) => ({
    ...c,
    items: companies.filter((co) => (co.effective_category || 'prospect_cold') === c.key)
  }));

  function flash(msg) { toast = msg; setTimeout(() => (toast = null), 3500); }

  async function loadCompanies() {
    loading = true; error = null;
    try {
      const r = await fetch('/api/admin/crm/companies', { credentials: 'include' });
      if (r.status === 403) { isAdmin = false; error = 'You do not have access to the CRM.'; return; }
      const data = await r.json();
      if (data.success) { companies = data.companies || []; isAdmin = true; }
      else error = data.error || 'Failed to load';
    } catch (e) { error = e.message; }
    finally { loading = false; }
  }

  async function loadActions() {
    try {
      const r = await fetch('/api/admin/crm/action-list', { credentials: 'include' });
      const data = await r.json();
      if (data.success) actionTasks = data.tasks || [];
    } catch (e) { flash('Failed to load action list'); }
  }

  async function openCompany(co) {
    selected = co; detail = null; detailLoading = true;
    overrideCat = co.category_override || '';
    overrideReason = co.category_override_reason || '';
    try {
      const r = await fetch(`/api/admin/crm/companies/${co.companyId}`, { credentials: 'include' });
      const data = await r.json();
      if (data.success) detail = data;
    } catch (e) { flash('Failed to load company'); }
    finally { detailLoading = false; }
  }
  function closeDrawer() { selected = null; detail = null; }

  async function saveOverride() {
    if (!selected) return;
    const body = overrideCat
      ? { category_override: overrideCat, category_override_reason: overrideReason || null }
      : { category_override: null, category_override_reason: null };
    const r = await fetch(`/api/admin/crm/companies/${selected.companyId}`, {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
    });
    const data = await r.json();
    if (data.success) {
      flash(overrideCat ? 'Override saved' : 'Override cleared');
      await loadCompanies();
      selected = companies.find((c) => c.companyId === selected.companyId) || selected;
    } else flash(data.error || 'Save failed');
  }

  async function bulk(action, label) {
    busy = label;
    try {
      const r = await fetch(`/api/admin/crm/${action}`, { method: 'POST', credentials: 'include' });
      const data = await r.json();
      if (data.success) {
        flash(`${label} done`);
        await loadCompanies();
        if (view === 'actions') await loadActions();
      } else flash(data.error || `${label} failed`);
    } catch (e) { flash(`${label} failed`); }
    finally { busy = null; }
  }

  async function completeTask(companyId, taskId) {
    await fetch(`/api/admin/crm/companies/${companyId}/tasks/${taskId}`, {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: 'done' })
    });
    await loadActions();
    if (detail && selected && selected.companyId === companyId) await openCompany(selected);
  }

  function fmtDate(d) { return d ? new Date(d).toLocaleDateString() : '—'; }
  function trialDaysLeft(sub) {
    if (!sub || !sub.trialEndDate) return null;
    return Math.ceil((new Date(sub.trialEndDate) - new Date()) / (1000 * 60 * 60 * 24));
  }

  onMount(async () => {
    try {
      const r = await fetch('/api/user', { credentials: 'include' });
      const data = await r.json();
      if (data.success) user = data.user;
    } catch (_) {}
    await loadCompanies();
  });
</script>

<Layout activeRoute="crm">
  <div class="container mx-auto px-4 py-8 max-w-[1400px]">
    <div class="flex items-center justify-between mb-6 flex-wrap gap-3">
      <div>
        <h1 class="text-2xl font-bold text-gray-900">Sales CRM</h1>
        <p class="text-sm text-gray-500">Buildzilla leads, trials and customers — internal.</p>
      </div>
      <div class="flex items-center gap-2">
        <div class="inline-flex rounded-lg border border-gray-200 overflow-hidden">
          <button class="px-3 py-1.5 text-sm {view === 'board' ? 'bg-gray-900 text-white' : 'bg-white text-gray-700'}"
                  on:click={() => (view = 'board')}>Pipeline</button>
          <button class="px-3 py-1.5 text-sm {view === 'actions' ? 'bg-gray-900 text-white' : 'bg-white text-gray-700'}"
                  on:click={() => { view = 'actions'; loadActions(); }}>Action list</button>
        </div>
        <button class="px-3 py-1.5 text-sm rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                disabled={!!busy} on:click={() => bulk('refresh-subscriptions', 'Refresh subscriptions')}>
          {busy === 'Refresh subscriptions' ? 'Refreshing…' : 'Refresh subscriptions'}
        </button>
        <button class="px-3 py-1.5 text-sm rounded-lg bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-50"
                disabled={!!busy} on:click={() => bulk('derive', 'Derive & tasks')}>
          {busy === 'Derive & tasks' ? 'Working…' : 'Derive + tasks'}
        </button>
      </div>
    </div>

    {#if loading}
      <div class="text-gray-500 py-20 text-center">Loading…</div>
    {:else if error}
      <div class="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3">{error}</div>
    {:else if view === 'board'}
      <div class="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-4">
        {#each byCategory as col}
          <div class="bg-gray-50 rounded-xl border border-gray-200 flex flex-col min-h-[120px]">
            <div class="px-3 py-2.5 flex items-center justify-between border-b border-gray-200">
              <span class="text-sm font-semibold text-gray-700">{col.label}</span>
              <span class="text-xs font-bold px-2 py-0.5 rounded-full border {badgeClass[col.color]}">{col.items.length}</span>
            </div>
            <div class="p-2 space-y-2 overflow-y-auto max-h-[65vh]">
              {#each col.items as co}
                <button class="w-full text-left bg-white rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-sm px-3 py-2 transition"
                        on:click={() => openCompany(co)}>
                  <div class="flex items-center justify-between gap-2">
                    <span class="font-medium text-gray-900 text-sm truncate">{co.name}</span>
                    {#if co.country}<span class="text-[10px] text-gray-400 uppercase">{co.country}</span>{/if}
                  </div>
                  {#if co.subscription_snapshot?.trialEndDate && co.effective_category === 'trial'}
                    <div class="text-[11px] text-blue-600 mt-0.5">Trial ends {fmtDate(co.subscription_snapshot.trialEndDate)}</div>
                  {/if}
                  {#if co.sales?.next_action}
                    <div class="text-[11px] text-gray-600 mt-1 line-clamp-2">→ {co.sales.next_action}</div>
                  {/if}
                  <div class="flex items-center gap-1.5 mt-1">
                    {#if co.sales}
                      <span class="text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700">AI</span>
                      {#if co.sales.stage}<span class="text-[10px] text-gray-500">{co.sales.stage}</span>{/if}
                      {#if co.sales.priority}<span class="text-[10px] px-1 rounded bg-gray-100 text-gray-600">P{co.sales.priority}</span>{/if}
                    {:else if co.category_is_override}
                      <span class="text-[10px] px-1.5 py-0.5 rounded bg-gray-900 text-white">confirmed</span>
                    {:else}
                      <span class="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 italic">derived</span>
                    {/if}
                    {#if co.bz_user_match}<span class="text-[10px] text-emerald-600">● linked</span>{/if}
                  </div>
                </button>
              {/each}
              {#if !col.items.length}<div class="text-xs text-gray-400 px-2 py-4 text-center">Empty</div>{/if}
            </div>
          </div>
        {/each}
      </div>
    {:else}
      <!-- Action list -->
      <div class="bg-white rounded-xl border border-gray-200 divide-y">
        {#if !actionTasks.length}
          <div class="px-4 py-10 text-center text-gray-400 text-sm">No open tasks. Run “Derive + tasks”.</div>
        {/if}
        {#each actionTasks as t}
          <div class="px-4 py-3 flex items-start justify-between gap-4">
            <div>
              <div class="text-sm font-medium text-gray-900">{t.title}</div>
              <div class="text-xs text-gray-500 mt-0.5">{t.detail}</div>
              <div class="text-[11px] text-gray-400 mt-1">Due {fmtDate(t.due_date)}</div>
            </div>
            <button class="text-xs px-2.5 py-1 rounded-md border border-gray-200 hover:bg-gray-50 whitespace-nowrap"
                    on:click={() => completeTask(t.companyId, t.taskId)}>Mark done</button>
          </div>
        {/each}
      </div>
    {/if}
  </div>

  <!-- Detail drawer -->
  {#if selected}
    <div class="fixed inset-0 z-40 bg-black/30" on:click={closeDrawer} on:keydown={() => {}} role="button" tabindex="-1"></div>
    <div class="fixed inset-y-0 right-0 z-50 w-full max-w-md bg-white shadow-2xl overflow-y-auto">
      <div class="px-5 py-4 border-b border-gray-200 flex items-start justify-between">
        <div>
          <h2 class="text-lg font-bold text-gray-900">{selected.name}</h2>
          <div class="flex items-center gap-2 mt-1">
            <span class="text-xs px-2 py-0.5 rounded-full border {badgeClass[catColor(selected.effective_category)]}">{catLabel(selected.effective_category)}</span>
            {#if selected.category_is_override}<span class="text-[10px] px-1.5 py-0.5 rounded bg-gray-900 text-white">confirmed</span>{/if}
            {#if selected.country}<span class="text-[10px] text-gray-400 uppercase">{selected.country}</span>{/if}
          </div>
        </div>
        <button class="text-gray-400 hover:text-gray-700 text-xl leading-none" on:click={closeDrawer}>×</button>
      </div>

      {#if detailLoading}
        <div class="p-6 text-gray-500 text-sm">Loading…</div>
      {:else if detail}
        <div class="p-5 space-y-5">
          <!-- Subscription panel -->
          <div class="rounded-lg border border-gray-200 p-3">
            <div class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Subscription</div>
            {#if detail.company.subscription_snapshot}
              {@const s = detail.company.subscription_snapshot}
              <div class="grid grid-cols-2 gap-2 text-sm">
                <div><span class="text-gray-400">Status</span><div class="font-medium">{s.status}</div></div>
                <div><span class="text-gray-400">Trial ends</span><div class="font-medium">{fmtDate(s.trialEndDate)}{#if trialDaysLeft(s) !== null} ({trialDaysLeft(s)}d){/if}</div></div>
                <div><span class="text-gray-400">Last login</span><div class="font-medium">{fmtDate(s.lastLogin)}</div></div>
                <div><span class="text-gray-400">Signed up</span><div class="font-medium">{fmtDate(s.userCreatedAt)}</div></div>
              </div>
              <div class="text-[11px] text-emerald-600 mt-2">Linked to {detail.company.bz_user_match?.username}</div>
            {:else}
              <div class="text-sm text-gray-400">No Buildzilla account matched. Run “Refresh subscriptions”.</div>
            {/if}
          </div>

          <!-- AI sales classification (from the email/SQLite engine) -->
          {#if detail.company.sales}
            {@const sa = detail.company.sales}
            <div class="rounded-lg border border-indigo-200 bg-indigo-50/40 p-3">
              <div class="flex items-center justify-between mb-2">
                <div class="text-xs font-semibold text-indigo-700 uppercase tracking-wide">AI sales engine</div>
                <div class="flex items-center gap-1.5">
                  {#if sa.stage}<span class="text-[11px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700">{sa.stage}</span>{/if}
                  {#if sa.priority}<span class="text-[11px] px-1.5 py-0.5 rounded bg-white border border-indigo-200 text-indigo-700">P{sa.priority}</span>{/if}
                </div>
              </div>
              {#if sa.next_action}
                <div class="text-sm text-gray-800 mb-2"><span class="text-gray-400">Next action:</span> {sa.next_action}</div>
              {/if}
              <div class="grid grid-cols-2 gap-2 text-xs text-gray-600">
                {#if sa.signup_status}<div><span class="text-gray-400">Signup</span><div class="font-medium">{sa.signup_status}</div></div>{/if}
                {#if sa.plan}<div><span class="text-gray-400">Plan</span><div class="font-medium">{sa.plan}</div></div>{/if}
                {#if sa.days_to_trial_end != null}<div><span class="text-gray-400">Trial days left</span><div class="font-medium">{sa.days_to_trial_end}</div></div>{/if}
                {#if sa.last_subject}<div class="col-span-2"><span class="text-gray-400">Last email</span><div class="font-medium truncate">{sa.last_subject}</div></div>{/if}
              </div>
              {#if sa.synced_at}<div class="text-[10px] text-gray-400 mt-2">Synced from engine {fmtDate(sa.synced_at)}</div>{/if}
            </div>
          {/if}

          <!-- Category override -->
          <div class="rounded-lg border border-gray-200 p-3">
            <div class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Category override</div>
            <p class="text-[11px] text-gray-400 mb-2">Derived: <span class="italic">{catLabel(detail.company.category)}</span>. Set an override to lock a value the mailbox can’t see (e.g. lost on a call). Survives re-sync.</p>
            <select bind:value={overrideCat} class="w-full text-sm rounded-md border border-gray-200 px-2 py-1.5 mb-2">
              <option value="">— no override (use derived) —</option>
              {#each CATEGORIES as c}<option value={c.key}>{c.label}</option>{/each}
            </select>
            {#if overrideCat}
              <input bind:value={overrideReason} placeholder="Reason (optional)" class="w-full text-sm rounded-md border border-gray-200 px-2 py-1.5 mb-2" />
            {/if}
            <button class="text-xs px-3 py-1.5 rounded-md bg-gray-900 text-white hover:bg-gray-800" on:click={saveOverride}>Save</button>
          </div>

          <!-- Notes -->
          {#if detail.company.status_note}
            <div class="rounded-lg border border-gray-200 p-3">
              <div class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Notes</div>
              <div class="text-sm text-gray-700 whitespace-pre-wrap">{detail.company.status_note}</div>
            </div>
          {/if}

          <!-- Contacts -->
          <div>
            <div class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Contacts ({detail.contacts.length})</div>
            <div class="space-y-1">
              {#each detail.contacts as c}
                <div class="text-sm flex items-center gap-2">
                  <span class="font-medium">{c.name || c.email}</span>
                  <span class="text-gray-400 text-xs">{c.email}</span>
                  {#if c.is_primary}<span class="text-[10px] text-blue-600">primary</span>{/if}
                </div>
              {/each}
            </div>
          </div>

          <!-- Tasks -->
          <div>
            <div class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Tasks</div>
            {#if !detail.tasks.length}<div class="text-sm text-gray-400">None</div>{/if}
            {#each detail.tasks as t}
              <div class="flex items-start justify-between gap-2 py-1.5 border-b border-gray-100 last:border-0">
                <div>
                  <div class="text-sm {t.status === 'done' ? 'line-through text-gray-400' : 'text-gray-800'}">{t.title}</div>
                  <div class="text-[11px] text-gray-400">Due {fmtDate(t.due_date)} · {t.status}</div>
                </div>
                {#if t.status === 'open'}
                  <button class="text-xs px-2 py-0.5 rounded border border-gray-200 hover:bg-gray-50" on:click={() => completeTask(t.companyId, t.taskId)}>Done</button>
                {/if}
              </div>
            {/each}
          </div>

          <!-- Signals -->
          {#if detail.signals.length}
            <div>
              <div class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Recent signals</div>
              {#each detail.signals.slice(0, 10) as s}
                <div class="text-xs text-gray-600 py-1 border-b border-gray-100 last:border-0">
                  <span class="font-medium">{s.type}</span> · {s.direction} · {fmtDate(s.occurred_at)}
                  {#if s.subject}<div class="text-gray-400 truncate">{s.subject}</div>{/if}
                </div>
              {/each}
            </div>
          {/if}
        </div>
      {/if}
    </div>
  {/if}

  {#if toast}
    <div class="fixed bottom-5 left-1/2 -translate-x-1/2 z-[60] bg-gray-900 text-white text-sm px-4 py-2 rounded-lg shadow-lg">{toast}</div>
  {/if}
</Layout>
